// src/pages/home/DataModal.js
import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import "./DataModal.css";
import Questionnaire from "./Questionnaire";
import { sendToBackend } from "../../api";
import config from "../../config.json";

const STAFF_PASSWORD = config.password;

// questionnaire labels
const START_LABEL = "Start time:";

// 🔹 Force all “today”/date logic to use America/New_York
const TIME_ZONE = "America/New_York";

function estISODate(dateObj) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(dateObj);

  const year = parts.find((p) => p.type === "year").value;
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;

  return `${year}-${month}-${day}`; // YYYY-MM-DD in NY
}

// MM/DD/YYYY in NY (your dog walk IDs use this)
function estUSDateString(dateObj) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(dateObj);
}

function capitalizeFirstLetter(input) {
  if (!input) return "";
  const lowercased = input.toLowerCase();
  return lowercased.charAt(0).toUpperCase() + lowercased.slice(1);
}

// ✅ Compare two timestamps as the same calendar day in America/New_York
function isSameDayEST(timestamp1, timestamp2) {
  const d1 = new Date(timestamp1);
  const d2 = new Date(timestamp2);
  return estISODate(d1) === estISODate(d2);
}

// ✅ Get a Date object representing "now" in America/New_York
function estNowDate() {
  // creates a Date whose *clock fields* match NY time (good for comparisons + hours/minutes)
  return new Date(new Date().toLocaleString("en-US", { timeZone: TIME_ZONE }));
}

// ✅ Today's ISO date in NY, computed at call-time (avoids midnight edge cases)
function estTodayISO() {
  return estISODate(estNowDate());
}

function isoDateToNumber(iso) {
  // "YYYY-MM-DD" -> YYYYMMDD as number for easy compare
  return Number(String(iso || "").replaceAll("-", ""));
}

// ✅ supports "04:02 PM" AND "17:01"
function timeStringToMinutes(t) {
  if (!t || typeof t !== "string") return null;

  const s = t.trim().toUpperCase();

  // 12-hour format: "4:02 PM" or "04:02 PM"
  let m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (m) {
    let hh = Number(m[1]);
    const mm = Number(m[2]);
    const ap = m[3];

    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    if (hh < 1 || hh > 12 || mm < 0 || mm > 59) return null;

    if (ap === "AM") {
      if (hh === 12) hh = 0;
    } else {
      if (hh !== 12) hh += 12;
    }

    return hh * 60 + mm;
  }

  // 24-hour format: "17:01"
  m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }

  return null;
}

function formatMinutesTo12h(mins) {
  let hh24 = Math.floor(mins / 60);
  const mm = mins % 60;

  const ampm = hh24 >= 12 ? "PM" : "AM";
  let hh12 = hh24 % 12;
  if (hh12 === 0) hh12 = 12;

  return `${hh12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

function prettyTimeString(t) {
  if (!t || typeof t !== "string") return "";
  const raw = t.trim();
  const mins = timeStringToMinutes(raw);
  if (mins == null) return raw;
  return formatMinutesTo12h(mins);
}

/* ✅ FIXED: Robustly finds Start time even if label punctuation/case differs.
   Works with Questionnaire.getAnswers() output:
   [{ question: "...", answer: "..." }]
*/
function extractStartTimeString(answers) {
  if (!Array.isArray(answers)) return null;

  const normalize = (s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/[:?]/g, ""); // remove ":" and "?"

  const target = normalize(START_LABEL); // "start time"

  for (const a of answers) {
    if (!a) continue;

    const label =
      (typeof a.label === "string" && a.label) ||
      (typeof a.question === "string" && a.question) ||
      "";

    const value =
      (typeof a.value === "string" && a.value) ||
      (typeof a.answer === "string" && a.answer) ||
      (typeof a.response === "string" && a.response) ||
      null;

    if (normalize(label) === target && value) return value;
  }

  return null;
}

// 🔹 Edit this to your real set of locations
const DEFAULT_LOCATIONS = ["Foster", "Manhattan", "Westhampton", "Wantagh"];

function DataModal({ onClose, mode = "dog" }) {
  const isCatMode = mode === "cat";

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const [animalList, setAnimalList] = useState([]);
  const [expandedAnimalId, setExpandedAnimalId] = useState(null);

  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const questionnaireRefs = useRef({});

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [activityType, setActivityType] = useState("Walk");

  const [currentLocation, setCurrentLocation] = useState("Foster");
  const [canChangeLocation, setCanChangeLocation] = useState(false);

  const [entryDate, setEntryDate] = useState(estTodayISO());
  const [canChangeDate, setCanChangeDate] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const abortRef = useRef(null);
  const runIdRef = useRef(0);

  // ✅ pretty popup state (instead of alert)
  const [popup, setPopup] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openPopup = (title, message) => {
    setPopup({ open: true, title: title || "", message: message || "" });
  };

  const closePopup = () => {
    setPopup((p) => ({ ...p, open: false }));
  };

  // ✅ Reload instead of close after successful save
  const reloadPage = () => {
    window.location.reload();
  };

  const storedLocationKey = "selectedLocation";
  useMemo(() => {
    const loc =
      localStorage.getItem(storedLocationKey)?.replace("In ", "") || "Foster";
    return `dataModalAnimals:v2:${isCatMode ? "cat" : "dog"}:${loc}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCatMode]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 90);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    async function fetchAnimals() {
      try {
        const storedLocation =
          localStorage.getItem(storedLocationKey)?.replace("In ", "") || "Foster";

        setCurrentLocation(storedLocation);

        try {
          const cached = localStorage.getItem(
            `dataModalAnimals:v2:${isCatMode ? "cat" : "dog"}:${storedLocation}`
          );
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed)) setAnimalList(parsed);
            else setAnimalList([]);
          } else {
            setAnimalList([]);
          }
        } catch {
          setAnimalList([]);
        }

        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const myRunId = ++runIdRef.current;

        const listEndpoint = isCatMode ? "/api/cats" : "/api/dogs";
        const response = await fetch(listEndpoint, { signal: ctrl.signal });
        const data = await response.json();

        if (ctrl.signal.aborted) return;
        if (myRunId !== runIdRef.current) return;

        const filtered = (data || [])
          .filter((a) => a.location === storedLocation)
          .map((a) => ({
            name: a.name,
            nameLower: String(a.name || "").toLowerCase(),
            location: a.location,
            id: a.id,
            img: a.image || `https://robohash.org/${a.name}?set=set4&size=40x40`,
            walked: a.lastWalked ? isSameDayEST(a.lastWalked, Date.now()) : false,
            walksToday: Array.isArray(a.walks) ? a.walks : [],
          }));

        setAnimalList(filtered);

        try {
          localStorage.setItem(
            `dataModalAnimals:v2:${isCatMode ? "cat" : "dog"}:${storedLocation}`,
            JSON.stringify(filtered)
          );
        } catch {}
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("Failed to fetch list:", error);
      }
    }

    setAnimalList([]);
    setSearch("");
    setDebouncedSearch("");
    setIsFocused(false);

    fetchAnimals();

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCatMode]);

  const handleSelectAnimal = (animal) => {
    if (!selectedAnimals.some((s) => s.id === animal.id)) {
      setSelectedAnimals([...selectedAnimals, animal]);
      setSearch("");
      setDebouncedSearch("");
    }
  };

  const handleRemoveAnimal = (animalToRemove) => {
    setSelectedAnimals(selectedAnimals.filter((a) => a.id !== animalToRemove.id));
    if (expandedAnimalId === animalToRemove.id) setExpandedAnimalId(null);
    delete questionnaireRefs.current[animalToRemove.id];
  };

  const filteredAnimals = useMemo(() => {
    if (!isFocused) return [];
    const term = String(debouncedSearch || "").toLowerCase().trim();

    const selectedIds = new Set(selectedAnimals.map((s) => s.id));

    const out = [];
    for (const a of animalList) {
      if (selectedIds.has(a.id)) continue;

      if (term) {
        const nl = a.nameLower ?? String(a.name || "").toLowerCase();
        if (!nl.includes(term)) continue;
      }

      out.push(a);
      if (out.length >= 60) break;
    }
    return out;
  }, [animalList, debouncedSearch, isFocused, selectedAnimals]);

  const handleEntryDateChange = (e) => {
    const newDate = e.target.value;
    if (!newDate) return;

    const todayISO = estTodayISO();

    if (isoDateToNumber(newDate) > isoDateToNumber(todayISO)) {
      openPopup("Oops!", "You can't select a future date (America/New_York).");
      e.target.value = entryDate;
      return;
    }

    if (newDate === todayISO) {
      setEntryDate(newDate);
      return;
    }

    if (canChangeDate) {
      setEntryDate(newDate);
      return;
    }

    const pwd = window.prompt(
      "Staff only: enter password to change the date for this entry."
    );

    if (pwd === STAFF_PASSWORD) {
      setCanChangeDate(true);
      setEntryDate(newDate);
    } else {
      openPopup("Not allowed", "Incorrect password. Date will stay as today.");
      e.target.value = entryDate;
    }
  };

  const handleLocationChange = (e) => {
    const newLoc = e.target.value;
    if (!newLoc || newLoc === currentLocation) return;

    if (canChangeLocation) {
      setCurrentLocation(newLoc);
      return;
    }

    const pwd = window.prompt(
      "Staff only: enter password to change the location of this entry."
    );
    if (pwd === STAFF_PASSWORD) {
      setCanChangeLocation(true);
      setCurrentLocation(newLoc);
    } else {
      openPopup("Not allowed", "Incorrect password. Location will stay the same.");
      e.target.value = currentLocation;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!firstName.trim() || !lastName.trim()) return;

    setIsSubmitting(true);

    try {
      const todayISO = estTodayISO();

      if (isoDateToNumber(entryDate) > isoDateToNumber(todayISO)) {
        openPopup(
          "Oops!",
          "That date is in the future (America/New_York). Please pick today or earlier."
        );
        return;
      }

      for (const a of selectedAnimals) {
        const qRef = questionnaireRefs.current[a.id];

        if (!qRef) {
          openPopup(
            "Almost there…",
            `The form for ${a.name} hasn't loaded yet.\n\nPlease expand the card and try again.`
          );
          return;
        }

        const issues = [];

        // ✅ 1) Future-time check FIRST
        if (entryDate === todayISO) {
          const answers = qRef.getAnswers?.() || [];
          const startTimeStr = extractStartTimeString(answers);
          const startMin = timeStringToMinutes(startTimeStr);

          if (startMin != null) {
            const now = estNowDate();
            const nowMin = now.getHours() * 60 + now.getMinutes();

            if (startMin > nowMin) {
             issues.push(
  `Start time error, can only put up to ${formatMinutesTo12h(nowMin)} (you put ${prettyTimeString(
    startTimeStr
  )}).`
);

            }
          }
        }

        // ✅ 2) Missing required fields
        if (typeof qRef.getMissingRequired === "function") {
          const missing = qRef.getMissingRequired();
          if (missing.length > 0) {
            issues.push(
              `Please complete all required questions:\n- ${missing.join(
                "\n- "
              )}`
            );
          }
        }

        if (issues.length > 0) {
          openPopup(`Fix ${a.name}'s entry`, issues.join("\n"));
          return;
        }
      }

      // DOG MODE
      if (!isCatMode) {
        for (const dog of selectedAnimals) {
          const activities = questionnaireRefs.current[dog.id]?.getAnswers() || [];

          await sendToBackend({
            id: dog.id,
            activities,
            firstName: capitalizeFirstLetter(firstName),
            lastName: capitalizeFirstLetter(lastName),
            activityType,
            walkDate: entryDate,
          });
        }

        reloadPage();
        return;
      }

      // CAT MODE
      for (const cat of selectedAnimals) {
        const activities = questionnaireRefs.current[cat.id]?.getAnswers() || [];

        const res = await fetch("/api/save-cat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: cat.id,
            activities,
            firstName: capitalizeFirstLetter(firstName),
            lastName: capitalizeFirstLetter(lastName),
            noteDate: entryDate,
          }),
        });

        if (!res.ok) {
          console.error("save-cat failed:", await res.text());
          openPopup("Save failed", `Failed to save for ${cat.name}.`);
          return;
        }
      }

      reloadPage();
    } catch (err) {
      console.error("Failed to save data:", err);
      openPopup("Uh oh", "Something went wrong while saving.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const locationOptions = Array.from(
    new Set([...(DEFAULT_LOCATIONS || []), currentLocation].filter(Boolean))
  );

  return (
    <div className="modal-overlay">
      <div className="modal">
        <button className="close-button" onClick={onClose}>
          ×
        </button>

        <h2>{isCatMode ? "Add Cat Note" : "Add Walk or Activity"}</h2>

        <div className="modal-scroll-area">
          <div className="name-fields">
            <input
              type="text"
              placeholder="First Name"
              className="name-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isSubmitting}
            />
            <input
              type="text"
              placeholder="Last Name"
              className="name-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="date-row">
            <label>
              <strong>{isCatMode ? "Note date:" : "Walk date:"}</strong>
            </label>
            <input
              type="date"
              className="walk-date-input"
              value={entryDate}
              max={estTodayISO()}
              onChange={handleEntryDateChange}
              disabled={isSubmitting}
            />
          </div>

          <div className="date-row">
            <label>
              <strong>{isCatMode ? "Cat location:" : "Location of walk:"}</strong>
            </label>
            <select
              className="walk-location-select"
              value={currentLocation}
              onChange={handleLocationChange}
              disabled={isSubmitting}
            >
              {locationOptions.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>

          {!isCatMode && (
            <div className="activity-toggle">
              <button
                className={`activity-option ${activityType === "Walk" ? "active" : ""}`}
                onClick={() => setActivityType("Walk")}
                type="button"
                disabled={isSubmitting}
              >
                Walk
              </button>
              <button
                className={`activity-option ${
                  activityType === "Indoor Activity" ? "active" : ""
                }`}
                onClick={() => setActivityType("Indoor Activity")}
                type="button"
                disabled={isSubmitting}
              >
                Indoor Activity
              </button>
              <button
                className={`activity-option ${
                  activityType === "Outdoor Activity" ? "active" : ""
                }`}
                onClick={() => setActivityType("Outdoor Activity")}
                type="button"
                disabled={isSubmitting}
              >
                Outdoor Activity
              </button>
            </div>
          )}

          <div className="dog-search-wrapper">
            <input
              ref={inputRef}
              type="text"
              placeholder={isCatMode ? "Search for a cat..." : "Search for a dog..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => setIsFocused(false), 150)}
              className="dog-search-input"
              disabled={isSubmitting}
            />
          </div>

          {filteredAnimals.length > 0 &&
            inputRef.current &&
            ReactDOM.createPortal(
              <ul
                className="suggestion-list"
                style={{
                  position: "absolute",
                  top:
                    inputRef.current.getBoundingClientRect().bottom +
                    window.scrollY +
                    "px",
                  left: inputRef.current.getBoundingClientRect().left + "px",
                  width: inputRef.current.offsetWidth + "px",
                  zIndex: 9999,
                }}
              >
                {filteredAnimals.map((a) => {
                  let message = "";

                  if (!isCatMode) {
                    const walkID = estUSDateString(new Date());

                    const walkAmount = a.walksToday.filter(
                      (i) => i.id?.includes?.(walkID) && i.type === "Walk"
                    );
                    const activityAmount = a.walksToday.filter(
                      (i) => i.id?.includes?.(walkID) && i.type === "Activity"
                    );

                    const walkMessage = walkAmount.length
                      ? `${walkAmount.length} walk${walkAmount.length > 1 ? "s" : ""}`
                      : "";
                    const activityMessage = activityAmount.length
                      ? `${activityAmount.length} activit${
                          activityAmount.length > 1 ? "ies" : "y"
                        }`
                      : "";

                    message =
                      walkMessage && activityMessage
                        ? `(${walkMessage}, ${activityMessage})`
                        : walkMessage || activityMessage
                        ? `(${walkMessage || activityMessage})`
                        : "";
                  }

                  return (
                    <li
                      key={a.id}
                      className="suggestion-item"
                      onMouseDown={() => !isSubmitting && handleSelectAnimal(a)}
                      style={{ cursor: "pointer", opacity: isSubmitting ? 0.6 : 1 }}
                    >
                      <img src={a.img} alt={a.name} className="dog-avatar" />
                      <span className="suggestion-name">{`${a.name} ${message}`}</span>
                    </li>
                  );
                })}
              </ul>,
              document.getElementById("dropdown-root")
            )}

          {selectedAnimals.map((a) => (
            <div key={a.id} className="dog-pill-card">
              <div
                className="dog-pill-header"
                onClick={() =>
                  !isSubmitting &&
                  setExpandedAnimalId(expandedAnimalId === a.id ? null : a.id)
                }
              >
                <img src={a.img} alt={a.name} className="pill-avatar" />
                <strong>{a.name}</strong>

                <span
                  className="dropdown-arrow"
                  style={{
                    transform:
                      expandedAnimalId === a.id ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  ▾
                </span>

                <span
                  className="remove-pill"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isSubmitting) handleRemoveAnimal(a);
                  }}
                >
                  ×
                </span>
              </div>

              <div
                className="dog-pill-body"
                style={{ display: expandedAnimalId === a.id ? "block" : "none" }}
              >
                <Questionnaire
                  dogName={a.name}
                  activityType={activityType}
                  location={currentLocation}
                  mode={mode}
                  ref={(ref) => (questionnaireRefs.current[a.id] = ref)}
                />
              </div>
            </div>
          ))}
        </div>

        {selectedAnimals.length > 0 && (
          <div className="submit-area">
            <button
              className="submit-button"
              onClick={handleSubmit}
              disabled={isSubmitting || !firstName.trim() || !lastName.trim()}
              type="button"
            >
              {isSubmitting ? "Saving..." : "Submit"}
            </button>
          </div>
        )}
      </div>

      {popup.open && (
        <div className="pretty-popup-overlay" onMouseDown={closePopup}>
          <div className="pretty-popup" onMouseDown={(e) => e.stopPropagation()}>
            {popup.title ? (
              <div className="pretty-popup-title">{popup.title}</div>
            ) : null}

            <div className="pretty-popup-body">
              {String(popup.message || "")
                .split("\n")
                .map((line, idx) => (
                  <div key={idx} className="pretty-popup-line">
                    {line === "" ? "\u00A0" : line}
                  </div>
                ))}
            </div>

            <button
              className="pretty-popup-close"
              onClick={closePopup}
              type="button"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataModal;
